import React, { useMemo, useRef, useState } from 'react';
import { Avatar, Button, Dropdown, Empty, Input, Modal, Skeleton, Tag, Tooltip, message } from 'antd';
import type { MenuProps } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookOpen,
  faClock,
  faDownload,
  faEllipsis,
  faEye,
  faFileArrowUp,
  faMagnifyingGlass,
  faPlus,
  faPen,
  faRotateLeft,
  faShareNodes,
  faStar,
  faTrash,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createNote, deleteNotePermanently, getNotes, restoreNote, saveNoteSecretBlock, setNoteFavorite, trashNote } from '../../services/notes';
import { Note, NoteBlock } from '../../types';
import NoteAccessDrawer from './NoteAccessDrawer';
import NoteView from './NoteView';
import { downloadMarkdownTemplate, ImportedSecret, markdownImportCreateInput, MAX_MARKDOWN_FILE_BYTES, parseMarkdownNote } from './markdownImport';
import './Notes.css';
import { notePath } from './noteUrls';

type NotesView = 'all' | 'mine' | 'shared' | 'favorites' | 'trash';

const views: Array<{ key: NotesView; label: string; icon: any }> = [
  { key: 'all', label: 'All notes', icon: faBookOpen },
  { key: 'mine', label: 'Created by me', icon: faUser },
  { key: 'shared', label: 'Shared with me', icon: faShareNodes },
  { key: 'favorites', label: 'Favorites', icon: faStar },
  { key: 'trash', label: 'Trash', icon: faTrash },
];

function relativeDate(date: string) {
  const elapsed = Date.now() - new Date(date).getTime();
  const days = Math.floor(elapsed / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: new Date(date).getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}

const Notes: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { notePath: selectedPath } = useParams<{ notePath?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<NotesView>('all');
  const [search, setSearch] = useState('');
  const [sharingItemId, setSharingItemId] = useState<string | null>(null);
  const importInput = useRef<HTMLInputElement>(null);
  const selectedReference = selectedPath || searchParams.get('note');
  const editing = searchParams.get('edit') === '1';
  const importing = searchParams.get('import') === '1';

  const { data: items = [], isLoading, isError } = useQuery({
    queryKey: ['notes', view],
    queryFn: () => getNotes({ view }),
  });

  const visibleItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(item =>
      item.title.toLowerCase().includes(needle) ||
      item.tags?.some(tag => tag.toLowerCase().includes(needle))
    );
  }, [items, search]);

  const selectNote = (note: Pick<Note, 'publicId' | 'title'> | null, shouldEdit = false, shouldSeedImport = false) => {
    if (!note) {
      navigate('/notes');
      return;
    }
    const next = new URLSearchParams();
    if (shouldEdit) next.set('edit', '1');
    if (shouldSeedImport) next.set('import', '1');
    navigate(`${notePath(note)}${next.size ? `?${next}` : ''}`);
  };

  const finishImportSeed = () => {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.delete('import');
      return next;
    }, { replace: true });
  };

  const setEditing = (shouldEdit: boolean) => {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      if (shouldEdit) next.set('edit', '1');
      else next.delete('edit');
      return next;
    }, { replace: true });
  };

  interface CreateRequest { title: string; content?: NoteBlock[]; tags?: string[]; secrets?: ImportedSecret[]; imported?: boolean }
  const createMutation = useMutation({
    mutationFn: async (request: CreateRequest) => {
      const note = await createNote({ title: request.title, content: request.content, tags: request.tags });
      const secretResults = await Promise.allSettled((request.secrets || []).map(secret => saveNoteSecretBlock(note.id, secret.blockId, secret.label, secret.value)));
      return { note, failedSecrets: secretResults.filter(result => result.status === 'rejected').length };
    },
    onSuccess: ({ note, failedSecrets }, request) => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      if (request.imported && failedSecrets) message.warning(`Imported “${note.title}”, but ${failedSecrets} protected ${failedSecrets === 1 ? 'value' : 'values'} could not be saved`);
      else if (request.imported) message.success(`Imported “${note.title}”`);
      selectNote(note, true, Boolean(request.imported));
    },
    onError: (error: any, request) => message.error(error.response?.data?.error || error.message || (request.imported ? 'Could not import Markdown' : 'Could not create note')),
  });

  const create = () => createMutation.mutate({ title: 'Untitled' });
  const importMarkdown = () => importInput.current?.click();
  const handleMarkdownFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!/\.(md|markdown)$/i.test(file.name)) {
      message.error('Choose a .md or .markdown file');
      return;
    }
    if (file.size > MAX_MARKDOWN_FILE_BYTES) {
      message.error('Markdown files must be 1 MB or smaller');
      return;
    }
    try {
      const imported = parseMarkdownNote(await file.text(), file.name);
      createMutation.mutate({ ...markdownImportCreateInput(imported), imported: true });
    } catch (error: any) {
      message.error(error.message || 'Could not read this Markdown file');
    }
  };
  const createMenu: MenuProps = {
    items: [
      { key: 'new', label: 'New Note', icon: <FontAwesomeIcon icon={faPlus} /> },
      { key: 'import', label: 'Import .md', icon: <FontAwesomeIcon icon={faFileArrowUp} /> },
      { type: 'divider' },
      { key: 'template', label: 'Download template', icon: <FontAwesomeIcon icon={faDownload} /> },
    ],
    onClick: ({ key }) => {
      if (key === 'new') create();
      else if (key === 'import') importMarkdown();
      else if (key === 'template') {
        downloadMarkdownTemplate();
        message.success('Markdown template downloaded');
      }
    },
  };

  return (
    <div className="library-page">
      <aside className="library-local-nav">
        <div className="library-nav-heading">
          <div><span>Workspace</span><strong>Notes</strong></div>
        </div>
        <nav aria-label="Note views">
          {views.map(item => (
            <button className={view === item.key ? 'is-active' : ''} key={item.key} onClick={() => { setView(item.key); selectNote(null); }}>
              <FontAwesomeIcon icon={item.icon} /><span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <main className="library-workspace">
        {selectedReference ? (
          <NoteView noteId={selectedReference} onClose={() => selectNote(null)} editing={editing} onEditingChange={setEditing} seedImportedContent={importing} onImportedContentSeeded={finishImportSeed} />
        ) : (
          <>
        <header className="library-toolbar">
          <div>
            <h1>{views.find(item => item.key === view)?.label}</h1>
            <p>{view === 'trash' ? 'Notes can be restored by their owner or manager.' : 'Persistent shared notes for the whole workspace.'}</p>
          </div>
          <Dropdown.Button menu={createMenu} type="primary" loading={createMutation.isPending} icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => create()}>New</Dropdown.Button>
        </header>

        <section className="library-list-section">
          <div className="library-list-toolbar">
            <Input
              allowClear
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              placeholder="Search titles and tags"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
            <span>{visibleItems.length} {visibleItems.length === 1 ? 'note' : 'notes'}</span>
          </div>

          <div className="library-list-header">
            <span>Name</span><span>Owner</span><span>Updated</span><span>Actions</span>
          </div>

          {isLoading ? (
            <div className="library-loading"><Skeleton active paragraph={{ rows: 7 }} /></div>
          ) : isError ? (
            <div className="library-empty"><Empty description="Notes could not be loaded" /></div>
          ) : visibleItems.length === 0 ? (
            <div className="library-empty">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={search ? 'No notes match this search' : view === 'trash' ? 'Trash is empty' : 'No notes here yet'}
              >
                {!search && view !== 'trash' && <Button type="primary" loading={createMutation.isPending} onClick={() => create()}>Create the first note</Button>}
              </Empty>
            </div>
          ) : (
            <div className="library-list">
              {visibleItems.map(item => (
                <NoteRow
                  key={item.id}
                  item={item}
                  onOpen={() => selectNote(item)}
                  onEdit={() => selectNote(item, true)}
                  onShare={() => setSharingItemId(item.id)}
                />
              ))}
            </div>
          )}
        </section>
          </>
        )}
      </main>
      <input ref={importInput} className="library-markdown-input" type="file" accept=".md,.markdown,text/markdown,text/plain" onChange={handleMarkdownFile} />
      <NoteAccessDrawer note={items.find(item => item.id === sharingItemId)} open={Boolean(sharingItemId)} onClose={() => setSharingItemId(null)} />
    </div>
  );
};

const NoteRow: React.FC<{ item: Note; onOpen: () => void; onEdit: () => void; onShare: () => void }> = ({ item, onOpen, onEdit, onShare }) => {
  const queryClient = useQueryClient();
  const canEdit = item.currentPermission === 'OWNER' || item.currentPermission === 'MANAGER' || item.currentPermission === 'EDITOR';
  const canManage = item.currentPermission === 'OWNER' || item.currentPermission === 'MANAGER';
  const tags = item.tags || [];
  const visibleTags = tags.slice(0, 3);
  const hiddenTags = tags.slice(3);
  type NoteAction = 'favorite' | 'moveToTrash' | 'restore' | 'deletePermanently';
  const itemMutation = useMutation({
    mutationFn: async (action: NoteAction) => {
      if (action === 'favorite') return setNoteFavorite(item.id, !item.isFavorite);
      if (action === 'restore') return restoreNote(item.id);
      if (action === 'deletePermanently') return deleteNotePermanently(item.id);
      return trashNote(item.id);
    },
    onSuccess: (_data, action) => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      const successMessage = action === 'favorite'
        ? (item.isFavorite ? 'Removed from favorites' : 'Added to favorites')
        : action === 'restore'
          ? 'Note restored'
          : action === 'deletePermanently'
            ? 'Note permanently deleted'
            : 'Moved to Trash';
      message.success(successMessage);
    },
    onError: (error: any) => message.error(error.response?.data?.error || 'Could not update note'),
  });
  const actionItems: MenuProps['items'] = [
    { key: 'open', label: 'Open', icon: <FontAwesomeIcon icon={faEye} /> },
    ...(canEdit && !item.deletedAt ? [{ key: 'edit', label: 'Edit', icon: <FontAwesomeIcon icon={faPen} /> }] : []),
    ...(canManage && !item.deletedAt ? [{ key: 'share', label: 'Share & access', icon: <FontAwesomeIcon icon={faShareNodes} /> }] : []),
    ...(!item.deletedAt ? [{ key: 'favorite', label: item.isFavorite ? 'Remove favorite' : 'Add to favorites', icon: <FontAwesomeIcon icon={faStar} /> }] : []),
    ...(canManage && !item.deletedAt ? [
      { type: 'divider' as const },
      { key: 'moveToTrash', danger: true, label: 'Move to Trash', icon: <FontAwesomeIcon icon={faTrash} /> },
    ] : []),
    ...(canManage && item.deletedAt ? [
      { type: 'divider' as const },
      { key: 'restore', label: 'Restore', icon: <FontAwesomeIcon icon={faRotateLeft} /> },
      { key: 'deletePermanently', danger: true, label: 'Delete permanently', icon: <FontAwesomeIcon icon={faTrash} /> },
    ] : []),
  ];
  const runAction: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'open') onOpen();
    else if (key === 'edit') onEdit();
    else if (key === 'share') onShare();
    else if (key === 'favorite') itemMutation.mutate('favorite');
    else if (key === 'moveToTrash') itemMutation.mutate('moveToTrash');
    else if (key === 'restore') itemMutation.mutate('restore');
    else if (key === 'deletePermanently') {
      Modal.confirm({
        title: 'Delete note permanently?',
        content: `“${item.title}” and its encrypted secret blocks will be deleted. This cannot be undone.`,
        okText: 'Delete permanently',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        centered: true,
        onOk: () => itemMutation.mutateAsync('deletePermanently'),
      });
    }
  };
  return (
    <div className="library-row" role="button" tabIndex={0} onClick={onOpen} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') onOpen(); }}>
      <span className="library-row-primary">
        <span className="library-row-copy">
          <strong>{item.title}{item.isFavorite && <FontAwesomeIcon icon={faStar} />}</strong>
          {tags.length > 0 && (
            <span className="library-row-tags">
              {visibleTags.map(tag => <Tag key={tag}>{tag}</Tag>)}
              {hiddenTags.length > 0 && (
                <Tooltip title={hiddenTags.join(', ')}>
                  <Tag className="library-row-tag-overflow">+{hiddenTags.length}</Tag>
                </Tooltip>
              )}
            </span>
          )}
        </span>
      </span>
      <span className="library-row-owner"><Tooltip title={item.owner.name}><Avatar size={25} src={item.owner.photoUrl}>{item.owner.name[0]}</Avatar></Tooltip></span>
      <span className="library-row-date"><FontAwesomeIcon icon={faClock} />{relativeDate(item.updatedAt)}</span>
      <span className="library-row-actions" onClick={event => event.stopPropagation()}>
        <Dropdown menu={{ items: actionItems, onClick: runAction }} placement="bottomRight" trigger={['click']}>
          <Button aria-label={`Actions for ${item.title}`} type="text" loading={itemMutation.isPending} icon={<FontAwesomeIcon icon={faEllipsis} />} />
        </Dropdown>
      </span>
    </div>
  );
};

export default Notes;
